import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, of, throwError, lastValueFrom } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';
import { ServiceDomainService } from '../domains/service.service';
import { SettingsService } from './settings.service';
import { MessageService } from 'primeng/api';
import { LoaderService } from './loader.service';
import { environment } from '../../environments/environment';

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private errorHandler: ErrorHandlerService,
    private serviceDomainService: ServiceDomainService,
    private settingsService: SettingsService,
    private messageService: MessageService,
    private loaderService: LoaderService
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.includes('/sendLog') || request.url.includes('log')) {
      return next.handle(request);
    }
    // Блокируем UI перед запросом
    this.loaderService.block();

    return next.handle(request).pipe(
      // Разблокируем UI после успешного ответа или ошибки
      finalize(() => {
        this.loaderService.unblock();
      }),
      catchError((err) => {
        this.loaderService.unblock();
        return this.catchErrorhandler(err);
      })
    );
  }

  // логирование ошибок и других сообщений в мессенджер. Если action = 'error', то в чат ошибок
  sendLog(action: string, data: string): Promise<void> {
    // Не отправляем логи в режиме разработки
    if (!environment.production) {
      console.log('[sendLog]', action, data);
      return Promise.resolve();
    }
    return lastValueFrom(
      this.serviceDomainService.sendLog(action, data)
        .pipe(
          catchError((error) => {
            if (action === 'error') {
              console.error('sendLog error:', error);
              return of(undefined);
            } else {
              return this.errorHandler.catchErrorhandler(error);
            }
          })
        )
    );
  }

  public catchErrorhandler = (err: HttpErrorResponse | Error | ErrorEvent | any) => {
    let errorMessage = this.settingsService.getTranslate('unknown_error', ['']);
    if (err) {
      if (err instanceof Error) {
        // Клиентская ошибка
        errorMessage = this.settingsService.getTranslate('error_err', [err.message || err.toString()]);
        this.sendLog('error', err.stack || err.toString());
        console.error(err.stack || err.toString());
      } else if (err instanceof HttpErrorResponse) {
        let errMess = err.error ? err.error.data || `${err.status}\n\n${err.message}` : `${err.status}\n\n${err.message}`;
        if (errMess.split('-|')[1]) {
          errorMessage = errMess.split('-|')[1];
        } else {
          errorMessage = this.settingsService.getTranslate('error_err', [errMess]);
          this.sendLog('error', errorMessage);
        }
        console.error(errorMessage);
      } else if (err.error instanceof ErrorEvent) {
        // Клиентская ошибка
        errorMessage = this.settingsService.getTranslate('error_err', [err.error.message]);
        this.sendLog('error', errorMessage);
        console.error(errorMessage);
      } else if (err.error && err.error.data) {
        // Ошибка базы данных
        errorMessage = this.settingsService.getTranslate('error_err', [
          `${err.status}\n\n${err.error.data.split('-|').length > 1 ? err.error.data.split('-|')[1] : err.error.data}`,
        ]);
        console.error(errorMessage);
      } else if (err.error) {
        // Серверная ошибка
        errorMessage = this.settingsService.getTranslate('error_err', [err.error.err || err.message]);
        console.error(errorMessage);
      } else {
        // Клиентская ошибка
        errorMessage = this.settingsService.getTranslate('error_err', [err]);
        this.sendLog('error', errorMessage);
        console.error(errorMessage);
      }
    }
    this.messageService.add({
      severity: 'error',
      summary: this.settingsService.translate[this.settingsService.selectedLang]['error'],
      detail: errorMessage,
    });

    return throwError(() => new Error(errorMessage)); // Возвращаем ошибку
  };
}
